import React, { memo, useCallback } from 'react';
import { TextField, InputAdornment, useTheme } from '@mui/material';
import { FormikProps } from 'formik';
import { Business } from '../../../data/types/domain';

type BusinessFormData = Omit<Business, "id" | "created_at" | "updated_at"> & {
  google_rating: number | null;
  google_reviews_count: number | null;
  trustpilot_rating: number | null;
  trustpilot_reviews_count: number | null;
};

interface BusinessFieldProps {
  name: keyof BusinessFormData;
  label: string;
  icon?: React.ReactNode;
  formik: FormikProps<{
    business: BusinessFormData;
  }>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  [key: string]: any;
}

export const BusinessField = memo(({ 
  name, 
  label, 
  icon,
  formik,
  onChange,
  type = 'text',
  ...props 
}: BusinessFieldProps) => {
  const theme = useTheme();
  const { values, setFieldValue, errors, touched } = formik;
  
  const fieldValue = values.business[name];
  const isTouched = touched?.business && (touched.business as Record<string, boolean>)[name];
  const hasError = isTouched && errors?.business && (errors.business as Record<string, string>)[name];
  const errorMessage = hasError ? (errors.business as Record<string, string>)[name] : '';

  const handleFieldChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    } else {
      setFieldValue(`business.${name}`, e.target.value);
    }
  }, [setFieldValue, name, onChange]);

  return (
    <TextField
      {...props}
      label={label}
      name={`business.${name}`}
      value={fieldValue ?? ''}
      onChange={handleFieldChange}
      error={!!hasError}
      helperText={errorMessage}
      variant="outlined"
      fullWidth
      type={type}
      inputProps={{
        ...props.inputProps,
        step: type === 'number' ? '0.1' : undefined,
        min: type === 'number' ? '0' : undefined,
        max: type === 'number' ? '5' : undefined,
      }}
      InputProps={{
        startAdornment: icon && (
          <InputAdornment position="start">
            {icon}
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
          },
        },
        '& .MuiInputBase-root': {
          borderRadius: 1.5,
        },
      }}
    />
  );
}); 